/**
 * Parse and shape orchestrator (execute_agent) stream output for storage in fouray_response.
 */

const normalizeFourayRawBuffer = (str) => {
  if (!str || typeof str !== "string") return str;
  let s = str.trim();
  s = s.replace(/"\s+([^"]+?)\s+"\s*:/g, (_, key) => `"${key.replace(/\s+/g, "")}":`);
  s = s.replace(/\\"\s*([^"\\]+?)\s*\\"\s*:/g, (_, key) => `"${key.replace(/\s+/g, "")}":`);
  s = s.replace(/\s*:\s*}\s*/g, ': ""}');
  s = s.replace(/\s*:\s*]\s*/g, ': ""]');
  return s;
};

/** Extract a balanced JSON object starting at or after startIndex. */
const extractBalancedJsonObject = (str, startIndex) => {
  let i = startIndex;
  while (i < str.length && /\s/.test(str[i])) i += 1;
  if (str[i] !== "{") return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let j = i; j < str.length; j += 1) {
    const ch = str[j];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === "\\" && inString) {
      escaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === "{") depth += 1;
    if (ch === "}") {
      depth -= 1;
      if (depth === 0) return str.substring(i, j + 1);
    }
  }
  return null;
};

const collectTableMetaSpans = (raw) => {
  const keyNeedle = '"__table_meta"';
  const spans = [];
  let searchFrom = 0;

  while (searchFrom < raw.length) {
    const keyStart = raw.indexOf(keyNeedle, searchFrom);
    if (keyStart < 0) break;

    const afterKey = raw.slice(keyStart + keyNeedle.length).match(/^\s*:/);
    if (!afterKey) {
      searchFrom = keyStart + 1;
      continue;
    }

    const valueStart = keyStart + keyNeedle.length + afterKey[0].length;
    const objStr = extractBalancedJsonObject(raw, valueStart);
    if (objStr) {
      spans.push({
        keyStart,
        valueEnd: valueStart + objStr.length,
      });
    }
    searchFrom = keyStart + 1;
  }

  return spans;
};

/**
 * Agent streams may emit duplicate "__table_meta" keys in one object.
 * JSON.parse keeps only the last — strip all meta entries, parse, then attach merged meta.
 */
const parseJsonWithMergedTableMeta = (raw) => {
  if (!raw || typeof raw !== "string") return null;

  const spans = collectTableMetaSpans(raw);
  if (spans.length <= 1) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  const mergedMeta = {};
  spans.forEach((span) => {
    const valueStart = span.keyStart + '"__table_meta"'.length;
    const afterKey = raw.slice(valueStart).match(/^\s*:/);
    if (!afterKey) return;
    const objStr = extractBalancedJsonObject(raw, valueStart + afterKey[0].length);
    if (!objStr) return;
    try {
      const parsed = JSON.parse(objStr);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        Object.assign(mergedMeta, parsed);
      }
    } catch {
      // skip unparseable meta fragment
    } 
  });

  let cleaned = raw;
  for (let i = spans.length - 1; i >= 0; i -= 1) {
    const span = spans[i];
    let removeStart = span.keyStart;
    let removeEnd = span.valueEnd;

    const trailingComma = cleaned.slice(removeEnd).match(/^\s*,/);
    if (trailingComma) {
      removeEnd += trailingComma[0].length;
    } else {
      const leadingComma = cleaned.slice(0, removeStart).match(/,\s*$/);
      if (leadingComma) removeStart -= leadingComma[0].length;
    }

    cleaned = cleaned.slice(0, removeStart) + cleaned.slice(removeEnd);
  }

  let parsed;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    // If stripping duplicate meta broke JSON (e.g. trailing commas), fall back to native parse.
    try {
      parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && Object.keys(mergedMeta).length > 0) {
        parsed.__table_meta = { ...(parsed.__table_meta || {}), ...mergedMeta };
      }
      return parsed;
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return parsed;
  if (Object.keys(mergedMeta).length > 0) {
    parsed.__table_meta = mergedMeta;
  }
  return parsed;
};

const parseFourayJson = (raw) => parseJsonWithMergedTableMeta(raw);

export const tryParseFourayRawBuffer = (str) => {
  if (!str || typeof str !== "string") return null;
  const raw = str.trim();
  if (!raw) return null;

  const direct = parseFourayJson(raw);
  if (direct) return direct;

  const first = raw.indexOf("{");
  const last = raw.lastIndexOf("}");
  if (first >= 0 && last > first) {
    const slice = raw.substring(first, last + 1);
    const sliced = parseFourayJson(slice);
    if (sliced) return sliced;
  }

  const s = normalizeFourayRawBuffer(raw);
  const normalized = parseFourayJson(s);
  if (normalized) return normalized;

  const sFirst = s.indexOf("{");
  const sLast = s.lastIndexOf("}");
  if (sFirst >= 0 && sLast > sFirst) {
    return parseFourayJson(s.substring(sFirst, sLast + 1));
  }

  return null;
};

/** Unwrap { response }, { summary: "json" }, and nested combinations. */
const unwrapParsedPayload = (parsed) => {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return parsed;
  }

  if (parsed.response != null && typeof parsed.response === "object" && !Array.isArray(parsed.response)) {
    return unwrapParsedPayload(parsed.response);
  }

  const keys = Object.keys(parsed);
  if (keys.length === 1 && parsed.summary && typeof parsed.summary === "string") {
    const inner = tryParseFourayRawBuffer(parsed.summary);
    if (inner) return unwrapParsedPayload(inner);
  }

  return parsed;
};

/** Extract structured JSON from orchestrator output (string, object, or fragmented SSE buffer). */
export const extractTeamFinalJson = (textOrObject) => {
  if (textOrObject && typeof textOrObject === "object" && !Array.isArray(textOrObject)) {
    return unwrapParsedPayload(textOrObject);
  }

  const trimmed = (textOrObject || "").trim();
  if (!trimmed) return {};

  const parsed = tryParseFourayRawBuffer(trimmed);
  if (parsed && typeof parsed === "object") {
    return unwrapParsedPayload(parsed);
  }

  // Avoid persisting useless fragments from incomplete SSE capture (e.g. "{")
  if (trimmed.length > 2) {
    return { summary: trimmed };
  }
  return {};
};

/** Run Agent save shape: { response: { ... } } — required by Playground field rendering. */
export const buildRunAgentFourayResponse = (data) => {
  const payload =
    data && typeof data === "object" && !Array.isArray(data)
      ? unwrapParsedPayload(data)
      : extractTeamFinalJson(data);

  return { response: payload };
};

const payloadJsonSize = (value) => {
  try {
    return JSON.stringify(value ?? "").length;
  } catch {
    return 0;
  }
};

/** Project-keyed orchestrator shape: { "PROJECT NAME": { folder_id, files: [...] } }. */
export const extractProjectEntityName = (payload) => {
  if (!payload || typeof payload !== "object") return "";

  const legacyName =
    payload.budget_variance_report?.project_name ||
    payload.invoice_reconciliation_report?.project_name ||
    payload.Invoice_reconciliation_report?.project_name ||
    payload.budget_variance?.project_name ||
    payload.invoice_reconciliation?.project_name;
  if (legacyName) return legacyName;

  const projectKey = Object.keys(payload).find(
    (key) =>
      !["multiple", "summary", "response"].includes(key) &&
      payload[key] &&
      typeof payload[key] === "object" &&
      (Array.isArray(payload[key].files) || payload[key].folder_id != null)
  );
  return projectKey || "";
};

const isTrivialSummaryPayload = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return false;
  const keys = Object.keys(payload);
  if (keys.length !== 1 || typeof payload.summary !== "string") return false;
  if (tryParseFourayRawBuffer(payload.summary)) return false;
  return payload.summary.trim().length < 10;
};

/** Merge split __table_meta objects that survived as nested siblings. */
const mergeTableMetaFields = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return payload;

  let merged =
    payload.__table_meta && typeof payload.__table_meta === "object" && !Array.isArray(payload.__table_meta)
      ? { ...payload.__table_meta }
      : {};

  Object.values(payload).forEach((value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return;
    const nestedMeta = value.__table_meta;
    if (nestedMeta && typeof nestedMeta === "object" && !Array.isArray(nestedMeta)) {
      merged = { ...merged, ...nestedMeta };
    }
  });

  if (Object.keys(merged).length === 0) return payload;
  return { ...payload, __table_meta: merged };
};

/** Flatten summary / multiple / records wrappers before persisting fouray_response.response. */
export const normalizeRunAgentPayloadForStorage = (payload) => {
  let result = unwrapParsedPayload(payload);
  if (!result || typeof result !== "object" || Array.isArray(result)) {
    return result ?? {};
  }

  if (Array.isArray(result.records)) {
    if (result.records.length === 1) {
      result = unwrapParsedPayload(result.records[0]);
    } else {
      return mergeTableMetaFields(result);
    }
  }

  if (Array.isArray(result.multiple)) {
    if (result.multiple.length === 1) {
      result = unwrapParsedPayload(result.multiple[0]);
    } else {
      return mergeTableMetaFields(result);
    }
  }

  if (isTrivialSummaryPayload(result)) {
    return {};
  }

  return mergeTableMetaFields(result);
};

/** Resolve parsed orchestrator payload without slicing to a single multiple[] item. */
export const resolveRunAgentStreamPayload = (finalResponse, rawBuffer = null) => {
  const candidates = [];

  if (typeof rawBuffer === "string" && rawBuffer.trim()) {
    const fromRaw = tryParseFourayRawBuffer(rawBuffer);
    if (fromRaw) candidates.push(fromRaw);
  }

  if (finalResponse && typeof finalResponse === "object" && !Array.isArray(finalResponse)) {
    if (
      Object.keys(finalResponse).length === 1 &&
      typeof finalResponse.summary === "string"
    ) {
      const inner = tryParseFourayRawBuffer(finalResponse.summary);
      if (inner) candidates.push(inner);
    } else {
      candidates.push(finalResponse);
    }
  } else if (typeof finalResponse === "string" && finalResponse.trim()) {
    const parsed = tryParseFourayRawBuffer(finalResponse);
    if (parsed) candidates.push(parsed);
    else return { summary: finalResponse.trim() };
  } else if (finalResponse != null) {
    const extracted = extractTeamFinalJson(finalResponse);
    if (extracted) candidates.push(extracted);
  }

  if (candidates.length === 0) return {};

  const resolved = candidates.reduce((best, current) =>
    payloadJsonSize(current) > payloadJsonSize(best) ? current : best
  );

  return normalizeRunAgentPayloadForStorage(resolved);
};

/** Flatten project-keyed orchestrator output for Playground field/table rendering. */
export const expandOrchestratorResponseForPlayground = (responseData) => {
  if (!responseData || typeof responseData !== "object" || Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData.multiple) && responseData.multiple.length === 1) {
    return expandOrchestratorResponseForPlayground(responseData.multiple[0]);
  }

  const keys = Object.keys(responseData).filter((k) => k !== "exception" && k !== "multiple");
  const projectKey = keys.find((k) => {
    const v = responseData[k];
    return v && typeof v === "object" && Array.isArray(v.files);
  });

  if (!projectKey) return responseData;

  const project = responseData[projectKey];
  const file = project.files?.[0];
  const expanded = { project_name: projectKey };

  if (project.folder_id != null) expanded.folder_id = project.folder_id;

  if (file?.budget_variance && typeof file.budget_variance === "object") {
    const bv = file.budget_variance;
    if (bv.totals && typeof bv.totals === "object") {
      Object.entries(bv.totals).forEach(([k, v]) => {
        expanded[`budget_${k}`] = v;
      });
    }
    if (Array.isArray(bv.variance_metrics)) expanded.variance_metrics = bv.variance_metrics;
    if (Array.isArray(bv.data_quality_issues)) {
      expanded.data_quality_issues = bv.data_quality_issues;
    }
    if (bv.status != null) expanded.budget_variance_status = bv.status;
  }

  if (file?.invoice_reconciliation && typeof file.invoice_reconciliation === "object") {
    const ir = file.invoice_reconciliation;
    if (Array.isArray(ir.missing_in_tracker)) {
      expanded.missing_in_tracker = ir.missing_in_tracker;
    }
    if (Array.isArray(ir.missing_in_books)) {
      expanded.missing_in_books = ir.missing_in_books;
    }
  }

  if (responseData.exception != null) expanded.exception = responseData.exception;

  return expanded;
};

/** Safe string for React text nodes — prevents "Objects are not valid as a React child". */
export const formatDisplayValue = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => formatDisplayValue(item)).join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

/** Regenerate / run-agent save: full orchestrator output in fouray_response.response. */
export const buildRunAgentRegenerateFourayResponse = (finalResponse, rawBuffer = null) => ({
  response:
    normalizeRunAgentPayloadForStorage(
      resolveRunAgentStreamPayload(finalResponse, rawBuffer)
    ) ?? {},
});

/** Items from { records: [...] }, { multiple: [...] }, or a single payload object. */
export const getRunAgentMultipleItems = (finalResponse) => {
  const parsed = extractTeamFinalJson(finalResponse);

  if (Array.isArray(parsed.records) && parsed.records.length > 0) {
    return parsed.records;
  }

  if (Array.isArray(parsed.multiple) && parsed.multiple.length > 0) {
    return parsed.multiple;
  }

  // Last resort: summary holds stringified JSON
  if (parsed.summary && typeof parsed.summary === "string") {
    const inner = tryParseFourayRawBuffer(parsed.summary);
    if (inner && Array.isArray(inner.records) && inner.records.length > 0) {
      return inner.records;
    }
    if (inner && Array.isArray(inner.multiple) && inner.multiple.length > 0) {
      return inner.multiple;
    }
    if (inner && typeof inner === "object") {
      return [inner];
    }
  }

  return [parsed];
};

/**
 * Bind fragmented SSE stream output to fouray_response objects.
 * Example output for one multiple item:
 * { fouray_response: { response: { budget_variance_report, line_items_variance, ... } } }
 */
export const bindRunAgentStreamResponse = (finalResponse) =>
  getRunAgentMultipleItems(finalResponse).map((item) => ({
    fouray_response: buildRunAgentFourayResponse(item),
  }));

/** Build bulk_create invoice rows from orchestrator output. */
export const buildRunAgentInvoiceEntries = (
  finalResponse,
  runId,
  agent,
  sessionId,
  query,
  source = "Invoice Processing",
  rawBuffer = null
) => {
  const fullPayload = resolveRunAgentStreamPayload(finalResponse, rawBuffer);
  const fullFouray = buildRunAgentRegenerateFourayResponse(finalResponse, rawBuffer);
  const items = getRunAgentMultipleItems(finalResponse);
  const documentDate = new Date().toISOString().slice(0, 10);
  const docNameBase = `doc - ${documentDate}`;

  return items.map((itemData, index) => {
    const projectName =
      extractProjectEntityName(itemData) || extractProjectEntityName(fullPayload);

    return {
      agent_id: agent.id,
      query,
      status: "completed",
      run_status: "success",
      entity_name: projectName,
      document_date: documentDate,
      agent_name: agent.name || "",
      source,
      fouray_response:
        items.length > 1 ? buildRunAgentFourayResponse(itemData) : fullFouray,
      session_id: sessionId,
      document_name: items.length > 1 ? `${docNameBase}-${index + 1}` : docNameBase,
      exception_status: null,
      chunks: null,
    };
  });
};

/** Pick the matching item from { multiple: [...] } when regenerating a single record. */
export const extractRunAgentItemFromFinalResponse = (finalResponse, currentFourayResponse = null) => {
  const items = getRunAgentMultipleItems(finalResponse);

  if (items.length <= 1) {
    return items[0] || {};
  }

  const current =
    currentFourayResponse?.response ?? currentFourayResponse ?? null;
  const currentProject = current?.budget_variance_report?.project_name;

  if (currentProject) {
    const matched = items.find(
      (item) => item?.budget_variance_report?.project_name === currentProject
    );
    if (matched) return matched;
  }

  return items[0];
};

/** Ensure Playground can read fouray_response.response after API load. */
export const normalizeFourayResponseForPlayground = (data) => {
  if (!data || typeof data !== "object") return data;

  const fr = data.fouray_response;
  if (!fr || typeof fr !== "object") return data;

  let response = fr.response != null ? fr.response : fr;

  // Fix records saved with { response: { summary: "{...json...}" } }
  if (response?.summary && typeof response.summary === "string") {
    const inner = tryParseFourayRawBuffer(response.summary);
    if (inner) {
      const unwrapped = unwrapParsedPayload(inner);
      if (Array.isArray(unwrapped.multiple) && unwrapped.multiple.length === 1) {
        response = unwrapped.multiple[0];
      } else if (Array.isArray(unwrapped.multiple) && unwrapped.multiple.length > 1) {
        response = unwrapped.multiple[0];
      } else {
        response = unwrapped;
      }
    }
  } else {
    response = unwrapParsedPayload(response);
    if (Array.isArray(response?.records) && response.records.length === 1) {
      response = response.records[0];
    } else if (Array.isArray(response?.multiple) && response.multiple.length === 1) {
      response = response.multiple[0];
    }
  }

  return {
    ...data,
    fouray_response: { response },
  };
};

/**
 * Flatten agent section wrappers for Playground:
 * { missing_in_tracker: { items: [...] } } → { missing_in_tracker: [...] }
 */
export const normalizeSectionTablesForPlayground = (responseData) => {
  if (!responseData || typeof responseData !== "object" || Array.isArray(responseData)) {
    return responseData;
  }

  const result = { ...responseData };
  let topTableMeta =
    result.__table_meta && typeof result.__table_meta === "object" && !Array.isArray(result.__table_meta)
      ? { ...result.__table_meta }
      : {};

  Object.keys(result).forEach((key) => {
    if (key === "__table_meta") return;

    const section = result[key];
    if (!section || typeof section !== "object" || Array.isArray(section)) return;

    const items = section.items;
    if (!Array.isArray(items)) return;
    if (items.length > 0 && (typeof items[0] !== "object" || items[0] == null)) return;

    const sectionMeta =
      section.__table_meta && typeof section.__table_meta === "object" && !Array.isArray(section.__table_meta)
        ? section.__table_meta
        : {};

    Object.assign(topTableMeta, sectionMeta);

    const metaKeys = Object.keys(sectionMeta);
    const tableKey =
      metaKeys.length === 1
        ? metaKeys[0]
        : key.endsWith("_section")
          ? key.slice(0, -"_section".length)
          : key;

    result[tableKey] = items;
    delete result[key];
  });

  if (Object.keys(topTableMeta).length > 0) {
    result.__table_meta = topTableMeta;
  }

  const irr =
    result.invoice_reconciliation_report ||
    result.Invoice_reconciliation_report ||
    result.invoice_reconciliation;
  if (irr?.project_name != null && irr.project_name !== "" && result.project_name == null) {
    result.project_name = irr.project_name;
  }
  if (irr && Object.keys(irr).length === 1 && irr.project_name != null) {
    delete result.invoice_reconciliation_report;
    delete result.Invoice_reconciliation_report;
    delete result.invoice_reconciliation;
  }

  return result;
};
